import { useEffect, useRef } from 'react'

export type NestedChromeDismissHandlers = {
  /** Close the open nested chrome and drop the held selection. */
  close: () => void
  /** Put the selection saved at open time back into the host. */
  restore: () => void
}

/** Chrome that belongs to the open layer: a press inside it is not a dismissal. */
const NESTED_CHROME_SELECTOR =
  '[data-editor-text-popover], [data-editor-text-flyout], [data-editor-text-modal-backdrop]'
const EDITING_BOX_SELECTOR =
  '[data-editor-text-box][data-editor-text-editing="true"]'
const TOOLBAR_SELECTOR = '[data-editor-text-toolbar]'
const MODAL_BACKDROP_SELECTOR = '[data-editor-text-modal-backdrop]'
const POPOVER_SELECTOR = '[data-editor-text-popover]'

/**
 * One dismissal rule for every popover and flyout the text-box toolbar opens:
 * a press outside the open chrome peels that one layer and nothing else — the
 * toolbar stays, the box stays in editing mode, the selection is unchanged.
 *
 * Capture-phase listeners on `document`, so they run before React's handlers
 * and before the bubble-phase outside-click guard on the same node:
 * - a press inside the popover, a body flyout or a modal backdrop is left
 *   alone;
 * - a press inside the editing box closes the chrome and goes through, so
 *   the caret lands where the user pressed;
 * - a press on the toolbar goes through untouched — the control it lands on
 *   closes the chrome itself (`toggleMenu` switches menus, the style buttons
 *   dismiss before they apply);
 * - any other press is swallowed: `preventDefault` keeps focus in the host,
 *   `stopPropagation` keeps it from starting a drag or a page selection, and
 *   the `click` it produces is stopped at capture so `useEditorOutsideClick`
 *   never sees it and the box is not deselected;
 * - Escape closes the chrome, except while a modal owns the key or while an
 *   input inside the popover has its own Escape handling.
 *
 * The click swallow outlives the popover on purpose: `close()` re-renders the
 * toolbar before the click arrives, so the listeners are installed for the
 * toolbar's lifetime and read `open` through a ref.
 */
export const useNestedChromeDismiss = (
  open: boolean,
  handlers: NestedChromeDismissHandlers,
) => {
  const openRef = useRef(open)
  openRef.current = open
  const handlersRef = useRef(handlers)
  handlersRef.current = handlers
  const swallowClickRef = useRef(false)

  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      if (!openRef.current) return
      const t = e.target
      if (!(t instanceof Element)) return
      if (t.closest(NESTED_CHROME_SELECTOR)) return
      if (t.closest(EDITING_BOX_SELECTOR)) {
        handlersRef.current.close()
        return
      }
      if (t.closest(TOOLBAR_SELECTOR)) return
      e.preventDefault()
      e.stopPropagation()
      swallowClickRef.current = true
      handlersRef.current.close()
      handlersRef.current.restore()
    }
    const onClick = (e: MouseEvent) => {
      if (!swallowClickRef.current) return
      swallowClickRef.current = false
      e.preventDefault()
      e.stopPropagation()
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (!openRef.current || e.key !== 'Escape') return
      if (document.querySelector(MODAL_BACKDROP_SELECTOR)) return
      const t = e.target
      if (
        t instanceof Element &&
        t.tagName === 'INPUT' &&
        t.closest(POPOVER_SELECTOR)
      )
        return
      e.preventDefault()
      e.stopPropagation()
      handlersRef.current.close()
      handlersRef.current.restore()
    }
    document.addEventListener('mousedown', onMouseDown, true)
    document.addEventListener('click', onClick, true)
    document.addEventListener('keydown', onKeyDown, true)
    return () => {
      document.removeEventListener('mousedown', onMouseDown, true)
      document.removeEventListener('click', onClick, true)
      document.removeEventListener('keydown', onKeyDown, true)
    }
  }, [])
}

export default useNestedChromeDismiss
